import { WallboxConfigurationModel } from 'src/Libs/wallboxes'
import { Box, Collapse, Typography } from '@mui/material'
import SettingsBox from 'src/Elements/Data/SettingsBox'
import { NumberField, ToggleField } from 'src/Libs/inputs/formik'
import React from 'react'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { useWallboxSettingsFormHelpers } from 'src/Libs/wallboxes/hooks'
import { UnitTypes } from 'src/Libs/types'
import { SETTING_RESTRICTED_TO_SERVER_TRANSLATION_KEY } from 'src/Libs/farm/constants'
import { SingleToggleSettingBox } from 'src/Elements/SystemSettings/forms'
import { WeekDaysSettingList } from '../../../form'
import { useDynamicPowerPricesEnabledContext } from 'src/Libs/dynamic-power-prices/context/dynamic-power-prices-enabled-provider'

interface Props {
  model: WallboxConfigurationModel
  displayCapacity?: boolean
  displayBidiMode?: boolean
  disabled?: boolean
}

/**
 * Display Charging Strategy settings for the old design (software version < X_2024_04)
 */
const HysteresisFieldSetWithoutWeekDayCard: React.FC<Props> = ({ model, displayCapacity = false, displayBidiMode = false, disabled = false }) => {
  const { t } = useCustomTranslation()
  const { activeChargeStrategies, weekdayVariant, doDisplayWeekdays } = useWallboxSettingsFormHelpers(model)
  const { isDynamicPowerPriceEnabledForWallboxes } = useDynamicPowerPricesEnabledContext()

  const activeChargeStrategyLabel = displayBidiMode
    ? 'systemSettings.wallbox.settings.activeChargeStrategyBidi.label'
    : 'systemSettings.wallbox.settings.activeChargeStrategy.label'

  const description = isDynamicPowerPriceEnabledForWallboxes
    ? 'systemSettings.wallbox.settings.activeChargeStrategy.description.dynamic-electricity-tariff'
    : 'systemSettings.wallbox.settings.activeChargeStrategy.description.static-electricity-tariff'

  return (
    <Box>
      <Typography variant="h2" sx={{ pb: 2 }}>
        {t('systemSettings.wallbox.settings.hysteresis.title')}
      </Typography>
      <SettingsBox
        disabled={disabled}
        title={t('systemSettings.wallbox.settings.chargeStopHysteresis')}
        description={description}
        gridSizes={{ mdLeft: 10, mdRight: 2, smLeft: 10, smRight: 2 }}
        suffix={
          <NumberField
            name="chargeStopHysteresis"
            label={t('systemSettings.wallbox.settings.chargeStopHysteresis')}
            suffix="min"
            disabled={disabled}
          />
        }
      />

      <SettingsBox
        disabled={disabled}
        title={t(activeChargeStrategyLabel)}
        suffix={<ToggleField values={activeChargeStrategies} name="activeChargeStrategy" optionDirection="column" disabled={disabled} />}
      />

      <Collapse in={doDisplayWeekdays}>
        <WeekDaysSettingList name="weekdays" variant={weekdayVariant} disabled={disabled} />
      </Collapse>

      {displayCapacity && (
        <SettingsBox
          disabled={disabled}
          title={t('systemSettings.wallbox.settings.diagBatCapacity')}
          suffix={
            <NumberField
              name="diagBatCapacity"
              label={t('systemSettings.wallbox.settings.diagBatCapacity')}
              suffix={UnitTypes.kiloWattHour}
              disabled={disabled}
            />
          }
        />
      )}
      {displayBidiMode && (
        <SingleToggleSettingBox
          title={t('systemSettings.wallbox.settings.bidiModeActive')}
          name="bidiModeActive"
          disabled={disabled}
          tooltipText={SETTING_RESTRICTED_TO_SERVER_TRANSLATION_KEY}
        />
      )}
    </Box>
  )
}

export default HysteresisFieldSetWithoutWeekDayCard
